import { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  constructor(props: ChunkErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Chunk loading error:', error, errorInfo);
  }

  isChunkError(): boolean {
    const { error } = this.state;
    if (!error) return false;

    const errorMessage = error.message || '';
    const errorStack = error.stack || '';

    return (
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('Importing module') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorStack.includes('chunk')
    );
  }

  handleRetry = (): void => {
    // Clear application cache and reload the page
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }

    // Force reload the page to get the latest chunks
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Check if it's a chunk loading error
      if (this.isChunkError()) {
        return (
          <div className="flex flex-col items-center justify-center w-full h-screen p-4 text-center">
            <h2 className="mb-4 text-2xl font-bold">Application Update Available</h2>
            <p className="mb-6 text-muted-foreground">
              A new version of the application is available. Please reload to get the latest updates.
            </p>
            <Button onClick={this.handleRetry} className="px-6 py-2">
              Reload Application
            </Button>
          </div>
        );
      }

      // For other errors, use the provided fallback or a generic message
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center w-full h-screen p-4 text-center">
            <p className="text-destructive">Something went wrong. Please try again later.</p>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
